import { Link } from "react-router-dom";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Cube, Check, ArrowRight, Printer } from "@phosphor-icons/react";

const ANNUAL_DISCOUNT = 0.15;
const annualOf = (monthly) => Math.round(monthly * 12 * (1 - ANNUAL_DISCOUNT) * 100) / 100;
const money = (n) => n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });


const PLANS = [
  { name: "Basic", base: 49, features: ["No per-asset fees", "QR codes & labels", "Basic reports"] },
  { name: "Essential", base: 149, features: ["No per-asset fees", "QR codes & labels", "Advanced reports + CSV", "Movement tracking"] },
  { name: "Pro", base: 299, popular: true, features: ["No per-asset fees", "QR codes & labels", "All reports + CSV", "Movement tracking", "Priority support"] },
  { name: "Enterprise", price: "Custom", from: 499, features: ["Unlimited assets & users", "Multi-store corporate dashboard", "Custom asset types", "Dedicated support", "API access"] },
];

const SERVICES = [
  { name: "Print & Ship", price: "$1.00", unit: "per label", desc: "We print your QR labels on the right stock and ship them ready to apply.", note: "Shipping & handling $7.95–$19.95 per shipment" },
  { name: "Print & Install", price: "$3.99", unit: "per asset", desc: "Label printing plus on-site installation on every asset.", note: "Label + installation" },
  { name: "Large-Volume On-Site Installation", price: "$2.99–$3.49", unit: "per asset", desc: "Volume pricing for lots, fleets and multi-location rollouts.", note: "Quoted by volume & distance" },
];

export default function Pricing() {
  const [annual, setAnnual] = useState(false);


  return (
    <div className="min-h-screen bg-[#FAFAFB] text-zinc-950" data-testid="pricing-page">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/70 border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <img src="/orbit-icon.jpg" alt="Orbit" className="h-9 w-9 object-contain rounded-md" />
            <span className="font-display font-bold text-sm tracking-tight"><span className="sm:hidden">Orbit</span><span className="hidden sm:inline">Orbit Inventory Management</span></span>
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/videos"><Button variant="ghost" className="h-10 font-semibold hidden sm:inline-flex">Videos</Button></Link>
            <Link to="/get-demo"><Button variant="ghost" className="h-10 font-semibold hidden sm:inline-flex">Book a Demo</Button></Link>
            <Link to="/login"><Button variant="ghost" className="h-10 font-semibold">Sign In</Button></Link>
            <Link to="/register"><Button className="h-10 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold">Start Free</Button></Link>
          </div>
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-5 py-16">
        <div className="text-center max-w-2xl mx-auto">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-600 mb-3">Pricing</p>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight">Simple plans that scale.</h1>
          <p className="mt-4 text-lg text-zinc-500">Flat monthly software from $49 — no per-asset fees. Labels and installation are optional add-on services. No contracts, cancel anytime.</p>
          <div className="mt-6 inline-flex items-center gap-1 rounded-full border border-zinc-200 bg-white p-1" data-testid="billing-toggle">
            <button onClick={() => setAnnual(false)} className={`h-9 px-4 rounded-full text-sm font-semibold transition-colors ${!annual ? "bg-zinc-950 text-white" : "text-zinc-600"}`} data-testid="toggle-monthly">Monthly</button>
            <button onClick={() => setAnnual(true)} className={`h-9 px-4 rounded-full text-sm font-semibold transition-colors flex items-center gap-1.5 ${annual ? "bg-zinc-950 text-white" : "text-zinc-600"}`} data-testid="toggle-annual">Annual <span className="text-[11px] font-bold text-emerald-500">Save 15%</span></button>
          </div>
        </div>

        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
          {PLANS.map((p) => (
            <div key={p.name} className={`p-7 rounded-xl border bg-white flex flex-col ${p.popular ? "border-emerald-500 ring-1 ring-emerald-500" : "border-zinc-200"}`} data-testid={`pricing-card-${p.name.toLowerCase()}`}>
              {p.popular && <span className="self-start text-xs font-bold uppercase tracking-wider text-emerald-600 mb-2">Most popular</span>}
              <h3 className="font-display font-bold text-xl">{p.name}</h3>
              <div className="mt-3 mb-5">
                {p.price === "Custom" ? (
                  <><span className="text-zinc-500 text-sm font-semibold">from </span><span className="font-display text-4xl font-extrabold">${p.from}+</span><span className="text-zinc-500"> / mo</span></>
                ) : annual ? (
                  <>
                    <span className="font-display text-4xl font-extrabold">${money(annualOf(p.base))}</span><span className="text-zinc-500"> / yr</span>
                    <div className="text-xs text-emerald-600 font-semibold mt-1">Save 15% vs ${money(p.base * 12)}/yr</div>
                  </>
                ) : (
                  <><span className="font-display text-4xl font-extrabold">${p.base}</span><span className="text-zinc-500"> / mo</span></>
                )}
              </div>
              <ul className="space-y-2.5 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-zinc-600"><Check size={18} weight="bold" className="text-emerald-500 shrink-0 mt-0.5" /> {f}</li>
                ))}
              </ul>
              {p.price === "Custom" ? (
                <Link to="/get-demo" className="mt-6 block" data-testid="pricing-contact-sales">
                  <Button className="w-full h-12 font-semibold bg-zinc-950 hover:bg-zinc-800 text-white">Contact Sales</Button>
                </Link>
              ) : (
                <Link to="/register" className="mt-6"><Button className={`w-full h-12 font-semibold ${p.popular ? "bg-emerald-500 hover:bg-emerald-600 text-white" : "bg-zinc-950 hover:bg-zinc-800 text-white"}`} data-testid={`plan-${p.name.toLowerCase()}-button`}>Get Started</Button></Link>
              )}
            </div>
          ))}
        </div>

        <div className="mt-10 text-center">
          <Link to="/get-demo"><Button variant="outline" className="h-12 px-6 border-zinc-300 font-semibold gap-2">Book a Demo <ArrowRight size={18} weight="bold" /></Button></Link>
        </div>
      </section>

      <section className="border-t border-zinc-200 bg-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-5 py-16">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-600 mb-3">Label & Installation Services</p>
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">Print it yourself, or let us handle the labels.</h2>
            <p className="mt-3 text-base md:text-lg text-zinc-500">Every plan includes unlimited DIY label printing. Add these services when you'd rather have labels printed, shipped or installed for you.</p>
          </div>
          <div className="mt-10 grid md:grid-cols-3 gap-6">
            {SERVICES.map((s) => (
              <div key={s.name} className="p-7 rounded-xl border border-zinc-200 bg-[#FAFAFB] flex flex-col" data-testid={`service-card-${s.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <div className="flex items-center gap-2 text-zinc-900"><Printer size={20} weight="bold" className="text-emerald-600" /><h3 className="font-display font-bold text-lg">{s.name}</h3></div>
                <div className="mt-4"><span className="font-display text-3xl font-extrabold">{s.price}</span><span className="text-zinc-500"> {s.unit}</span></div>
                <p className="mt-3 text-sm text-zinc-600 flex-1">{s.desc}</p>
                <p className="mt-3 text-xs font-semibold text-zinc-500 border-t border-zinc-200 pt-3">{s.note}</p>
              </div>
            ))}
          </div>
          <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4">
            <Link to="/request-quote" data-testid="services-request-quote"><Button className="h-12 px-6 bg-zinc-950 hover:bg-zinc-800 text-white font-semibold gap-2">Request a quote <ArrowRight size={18} weight="bold" /></Button></Link>
            <p className="text-sm text-zinc-500">Volume, label stock and travel affect final service pricing. Software plans are billed separately.</p>
          </div>
        </div>
      </section>


      <footer className="bg-zinc-950 text-zinc-500 border-t border-zinc-800">
        <div className="max-w-7xl mx-auto px-5 py-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm">
          <div className="flex items-center gap-2"><Cube size={18} weight="bold" className="text-emerald-600" /> © {new Date().getFullYear()} Orbit Inventory Management</div>
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-3">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <Link to="/videos" className="hover:text-white transition-colors">Videos</Link>
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
